<template>
  <div
    class="content-table"
    style="height: 100%;">

    <el-divider v-if="isEmptySearchResult"/>
    <div
      v-if="isEmptySearchResult"
      class="content-table-no-data-container">
      <div class="content-table-no-data-container-body">
        <div class="content-table-no-data-image" />
        <div class="mt-24 el-text el-text--display-regular">
          {{ $t('wr-dashboard.search.message.empty') }}
        </div>
      </div>
    </div>

    <el-table
      row-key="id"
      v-if="!isEmptySearchResult"
      style="height: 100%;"
      height="fit-scrollable"
      :data="contentList"
      highlight-current-row
      :default-sort = "{ prop: 'displayName', order: 'ascending'}"
      @row-click="onTableRowClick"
      @row-dblclick="onTableRowDblClick"
      @select="handleSelect"
      @select-all="handleSelect"
      @sort-change="handleSortChange"
      ref="table"
      :empty-text="$t('wr-dashboard.content-table.message.empty-folder')"
      :loading-append="isTableDataLoading"
      border-bottom>

      <el-table-column
        type="selection"
        width="64"
      />

      <el-table-column
        prop="id"
        label="id"
        hidden/>

      <el-table-column
        prop="folderItem"
        label="folderItem"
        hidden/>

      <el-table-column
        prop="notarized"
        label="notarized"
        hidden/>

      <el-table-column
        prop="crtInternalId"
        label="crtInternalId"
        hidden/>

      <el-table-column
        prop="certificateId"
        label="certificateId"
        hidden/>

      <el-table-column
        prop="sequencer"
        label="sequencer"
        hidden/>

      <el-table-column
        prop="displayName"
        :label="nameColumnLabel"
        :sort-method="() => 0"
        align="left"
        sortable>
        <template slot-scope="{ row }">

          <div style="display: inline-block;">
            <el-icon
              :name="getContentIconName(row)"
              class="content-table-file-icon"/>
            <el-icon
              v-if="row.itemGroup === 'version'"
              class="content-backup-version-icon"
              name="clr-time-clr--16"
            />
          </div>

          <span
            class="ml-16"
            v-highlight="searchValue">
            {{ row.displayName }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="status"
        align="right"
        width="64"
        :hidden="statusColumnHidden"
        label="">
        <template slot-scope="{ row }">
          <div
            v-if="row.notarized">
            <el-icon
              slot="icon"
              color="brand-light"
              name="i-license--16"/>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :hidden="pathColumnHidden"
        prop="path"
        :sort-method="() => 0"
        :label="$t('wr-dashboard.content-table.column.path')"
        align="left"
        sortable>
        <template slot-scope="{ row }">
          {{ row.id }}
        </template>
      </el-table-column>

      <el-table-column
        prop="size"
        align="right"
        width="180"
        :label="$t('wr-dashboard.content-table.column.size')"
        :sort-method="() => 0"
        sortable>
        <template slot-scope="{ row }">
          <span v-if="!row.folderItem">
            {{ row.size | size }}
          </span>
          <span v-else />
        </template>
      </el-table-column>

      <el-table-column
        prop="lastVersion"
        align="left"
        width="280"
        :label="$t('wr-dashboard.content-table.column.version')"
        :sort-method="() => 0"
        sortable>
        <template slot-scope="{ row }">
          <span v-if="row.itemGroup !== 'volume'">
            {{ row.backUpDate | date }}
          </span>
          <span v-else />
        </template>
      </el-table-column>

    </el-table>


    <notary-verify
      v-if="isNotaryVerifyScreenVisible"
      :archive-id="archiveId"
      :path="activeFilePath"
      :file-version="activeFileVersion"
      :file-name="activeFileName"
      :crt-internal-id="activeNotaryItem.crtInternalId"
      :file-size-bytes="activeNotaryItem.fileSizeBytes"
      :notary-url="getAppFeatures.notaryUrl"
      :is-enterprise-mode="isMspMode"
      @handleClose="handleNotaryVerifyScreenClose"
    />

    <versions-dialog-box
      v-if="isVersionsDialogBoxVisible"
      :file-name="activeFileName"
      :file-versions="getFileVersions"
      :app-features="getAppFeatures"
      @verify="notaryVerify"
      @certificate="openFileCertificatePage"
      @asign="asign"
      @restore="restore"
      @handleClose="handleVersionsDialogBoxClose"
    />

    <a-sign-dialog-box
      v-if="isASignDialogBoxVisible"
      :archive-id="archiveId"
      :path="activeFilePath"
      :file-version="activeFileVersion"
      :file-name="activeFileName"
      @handleClose="handleASignDialogBoxClose"
    />

    <el-sidebar-layout
      class="el-sidebar"
      v-if="isSideBarOpen"
      open
      @close="sidebarClose()">

      <template #sidebar>
        <el-sidebar-panel
          @command="handleSidebarCommand"
          :title="sidebarTitle"
          :subtitle="sidebarSubTitle"
          show-close
          :actions="sidebarActions"
          :properties="sidebarProps">
          <template
            #preview
            v-if="isSideBarPreviewEnabled">
            <div class="el-sidebar-preview">

              <div style="display: inline-block;">
                <el-icon
                  size="128"
                  class="el-sidebar-icon"
                  :name="sidebarPreviewIconName"/>
                <el-icon
                  v-if="isSidebarVersionSelected"
                  class="sidebar-backup-version-icon"
                  size="32"
                  name="clr-time-clr--16"
                />
              </div>

            </div>
          </template>
        </el-sidebar-panel>
      </template>
    </el-sidebar-layout>

  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import NotaryVerify from './NotaryVerify';
import VersionsDialogBox from './VersionsDialogBox';
import ASignDialogBox from './ASignDialogBox';

import {
  FETCH_FILE_VERSIONS,
  UPDATE_ACTIVE_VERSION_PATH,
  UPDATE_ACTIVE_VOLUME_PATH,
  SORT_CONTENT_ITEMS
} from '../store/action-types';

import {
  PENDING_STATUS
} from '../store/action-status';

const { mapGetters, mapActions } = createNamespacedHelpers('wr-dashboard');

export default {
  name: 'ContentTable',
  components: {
    NotaryVerify,
    VersionsDialogBox,
    ASignDialogBox
  },
  props: {
    archiveId: {
      type: String,
      default: '',
      required: true
    },
    boxType: {
      type: String,
      default: 'BACKUP',
      required: true
    },
    boxGroup: {
      type: String,
      default: 'BACKUPS',
      required: false
    },
    statusColumnHidden: {
      type: Boolean,
      default: true,
      required: true
    },
    pathColumnHidden: {
      type: Boolean,
      default: true,
      required: true
    },
    contentList: {
      type: Array,
      default: () => [],
      required: true
    },
    searchValue: {
      type: String,
      default: '',
      required: false
    }
  },
  data() {
    return {
      activeFileName: '',
      activeFilePath: '',
      activeFileVersion: 0,
      isActiveFolderItem: false,
      isNotaryVerifyScreenVisible: false,
      isVersionsDialogBoxVisible: false,
      isASignDialogBoxVisible: false,
      activeFileVersionsItem: {},
      activeNotaryItem: {
        crtInternalId: '',
        fileSizeBytes: 0
      },
      selectedItems: [],
      activeLinkId: 0,
      isMspMode: true
    };
  },
  computed: {
    ...mapGetters([
      'getFileVersions',
      'getFetchFileVersionsStatus',
      'getFetchContentItemsStatus',
      'getSearchResultReceived',
      'getAppFeatures'
    ]),
    nameColumnLabel() {
      const content = this.contentList;

      if (content.length > 0) {
        if (content[0].itemGroup === 'version') {
          return this.$t('wr-dashboard.content-table.column.versions');
        } else if (content[0].itemGroup === 'volume') {
          return this.$t('wr-dashboard.content-table.column.volumes');
        }
      }

      return this.$t('wr-dashboard.content-table.column.name');
    },
    isEmptySearchResult() {
      return this.getSearchResultReceived &&
        this.getFetchContentItemsStatus !== PENDING_STATUS &&
        this.contentList.length === 0;
    },
    isTableDataLoading() {
      return this.getFetchContentItemsStatus === PENDING_STATUS;
    },
    isSideBarOpen() {
      return this.selectedItems.length > 0;
    },
    isSideBarPreviewEnabled() {
      return this.selectedItems.length === 1;
    },
    isSidebarVersionSelected() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return row.itemGroup === 'version';
      }
      return false;
    },
    sidebarPreviewIconName() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.getContentIconName(row);
      }
      return '';
    },
    sidebarTitle() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return row.displayName;
      }
      return this.$tc(
        'wr-dashboard.side-bar.selected.title:plural',
        this.selectedItems.length, { count: this.selectedItems.length }
      );
    },
    sidebarSubTitle() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return row.id;
      }
      return '';
    },
    sidebarProps() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        if (!row.folderItem) {
          return [
            {
              label: this.$t('wr-dashboard.content-table.label.size'),
              value: this.$options.filters.size(row.size)
            },
            {
              label: this.$t('wr-dashboard.content-table.label.created'),
              value: this.$options.filters.date(row.creationDate)
            },
            {
              label: this.$t('wr-dashboard.content-table.label.modified'),
              value: this.$options.filters.date(row.modifiedDate)
            }
          ];
        }
        return [
          {
            label: this.$t('wr-dashboard.content-table.label.modified'),
            value: this.$options.filters.date(row.modifiedDate)
          }
        ];
      }
      return [];
    },
    sidebarActions() {
      const actions = [];

      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.getMenuActions(row);
      }

      const row = this.selectedItems[0];
      if (!this.isVersionOrVolumeItem(row)) {
        actions.push({
          label: this.$t('wr-dashboard.content-table.action.restore'),
          command: 'restore-selected',
          icon: 'i-download--16'
        });
      }

      return actions;
    }
  },
  watch: {
    getFetchContentItemsStatus(status) {
      if (status === PENDING_STATUS) {
        this.sidebarClose();
      }
    }
  },
  methods: {
    ...mapActions({
      fetchFileVersions: FETCH_FILE_VERSIONS,
      updateActiveVersionPath: UPDATE_ACTIVE_VERSION_PATH,
      updateActiveVolumePath: UPDATE_ACTIVE_VOLUME_PATH,
      sortContentItems: SORT_CONTENT_ITEMS
    }),
    getContentIconName(row) {
      if (row.itemGroup === 'version') {
        return this.$options.filters.archiveIcon('', this.boxType);
      } else if (row.itemGroup === 'volume') {
        return this.$options.filters.fileIcon('', 'volume');
      } else if (row.folderItem) {
        return this.$options.filters.fileIcon('', 'folder');
      }
      return this.$options.filters.fileIcon(row.name, '');
    },
    sidebarClose() {
      this.$refs.table.clearSelection();
      this.selectedItems = [];
    },
    handleSelect(selectedItems) {
      this.selectedItems = selectedItems;
    },
    getMenuActions(row) {
      const actions = [];

      if (!this.isVersionOrVolumeItem(row)) {
        actions.push({
          label: this.$t('wr-dashboard.content-table.action.restore'),
          command: 'restore',
          icon: 'i-download--16'
        });
      }

      if (this.getAppFeatures.notaryEnabled && row.notarized) {
        actions.push({
          label: this.$t('wr-dashboard.content-table.action.notary-verify'),
          command: 'notary-verify',
          icon: 'i-license--16'
        });
      }

      if (this.getAppFeatures.notaryEnabled && row.notarized) {
        actions.push({
          label: this.$t('wr-dashboard.content-table.action.notary-get-crt'),
          command: 'notary-get-crt',
          icon: 'i-file-notarization-o--16'
        });
      }

      if (!row.folderItem) {
        actions.push({
          label: this.$t('wr-dashboard.content-table.action.versions'),
          command: 'versions',
          icon: 'i-clock-o--16'
        });
      }

      if (!row.folderItem && this.getAppFeatures.asignEnabled && row.size > 0) {
        actions.push({
          label: this.$t('wr-dashboard.content-table.action.asign'),
          command: 'asign',
          icon: 'i-esign-o--16'
        });
      }

      return actions;
    },
    isVersionOrVolumeItem(tableRow) {
      return tableRow.itemGroup === 'version' || tableRow.itemGroup === 'volume';
    },
    handleSidebarCommand(command) {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        this.handleAction(command, row);
      } else if (command === 'restore-selected') {
        this.restoreSelected(0, this.selectedItems);
      }

      if (command !== 'versions' && command !== 'asign') {
        this.sidebarClose();
      }
    },
    handleAction(command, tableRow) {
      this.activeFileName = tableRow.displayName;
      this.activeFilePath = tableRow.id;
      this.isActiveFolderItem = tableRow.folderItem;
      this.activeFileVersion = 0;

      if (command === 'notary-verify') {
        this.notaryVerify(tableRow, 0);
      } else if (command === 'notary-get-crt') {
        this.openFileCertificatePage(tableRow.crtInternalId);
      } else if (command === 'versions') {
        this.fetchFileVersions({
          archiveId: this.archiveId,
          path: tableRow.id
        });
        this.showVersionsDialogBox(true);
      } else if (command === 'asign') {
        this.asign(0);
      } else if (command === 'restore') {
        this.restore(0, tableRow.folderItem);
      }
    },
    asign(version) {
      this.activeFileVersion = version;
      this.showVersionsDialogBox(false);
      this.showASignDialogBox(true);
    },
    restore(version, isFolder) {
      this.activeFileVersion = version;
      this.download(
        this.archiveId,
        this.activeFilePath + (isFolder ? '/' : ''),
        this.activeFileVersion
      );
    },
    restoreSelected(version, rowArray) {
      this.activeFileVersion = version;

      const rowIds = rowArray.map(function (row) {
        return row.id + (row.folderItem ? '/' : '');
      });

      this.download(
        this.archiveId,
        rowIds,
        this.activeFileVersion
      );
    },
    // version: set to 0 - to restore latest file, or long unixTimestamp in ms
    // path: files/folders path or path array. (The folder paths should ends '/')
    download(archiveId, path, version) {
      const baseURL = `${window.location.origin + window.location.pathname}ui`;
      const element = document.createElement('a');

      let pathArgs = '';
      if (Array.isArray(path)) {
        path.forEach(function (item) {
          pathArgs += `&path=${encodeURIComponent(item)}`;
        });
      } else {
        pathArgs = `&path=${encodeURIComponent(path)}`;
      }

      element.setAttribute('href', `${baseURL
      }/restore?archiveId=${encodeURIComponent(archiveId)}${pathArgs}&ver=${version}`);
      // element.setAttribute('download', '');
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    },
    showNotaryScreen(show) {
      this.isNotaryVerifyScreenVisible = show;
    },
    handleNotaryVerifyScreenClose() {
      this.showNotaryScreen(false);
    },
    showVersionsDialogBox(show) {
      this.isVersionsDialogBoxVisible = show;
    },
    handleVersionsDialogBoxClose() {
      this.showVersionsDialogBox(false);
    },
    showASignDialogBox(show) {
      this.isASignDialogBoxVisible = show;
    },
    handleASignDialogBoxClose() {
      this.showASignDialogBox(false);
    },
    notaryVerify(row, version) {
      this.activeFileVersion = version;
      this.activeNotaryItem = {
        crtInternalId: row.crtInternalId,
        fileSizeBytes: row.size
      };
      this.showVersionsDialogBox(false);
      this.showNotaryScreen(true);
    },
    openFileCertificatePage(crtInternalId) {
      this.$emit('certificate', crtInternalId);
      window.open(`${window.location.origin + window.location.pathname}#/enterprise/certificate/${crtInternalId}`, '_blank');
    },
    handleSortChange(column) {
      this.sortContentItems({
        items: this.contentList,
        columnName: column.prop,
        sortOrder: column.order
      });
    },
    onTableRowClick(row, event, column) {
      if (column.prop === 'displayName' && row.folderItem) {
        this.openFolder(row);
      } else {
        this.$refs.table.clearSelection();
        this.selectedItems = [row];
      }
    },
    onTableRowDblClick(row) {
      this.openFolder(row);
    },
    openFolder(row) {
      if (row.folderItem) {
        this.sidebarClose();
        if (row.itemGroup === 'version') {
          this.updateActiveVersionPath(row.id);
        } else if (row.itemGroup === 'volume') {
          this.updateActiveVolumePath(row.id);
        }
        this.$emit('folderSelected', {
          archiveId: this.archiveId,
          path: row.id,
          displayName: row.displayName
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
  .content-table-no-data-container {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }

  .content-table-no-data-container-body {
    margin-left: 256px;
    margin-top: -78px;
  }

  .content-table-no-data-image {
    margin: auto;
    background: url('../assets/empty/empty-search-result.png') no-repeat;
    width: 128px;
    height: 128px;
  }
</style>
